import React, { useEffect, useState } from 'react'
import { fetchUserSkills, getStoredUser, getCurrentUserId } from '../../SkillsApi/SkillsApi'
import './ProfilePage.css'

const MODULE_TITLES = {
  1: 'Module 1: Getting Started',
  2: 'Module 2: Core Concepts',
  3: 'Module 3: Deep Dive',
  4: 'Module 4: Applied Practice',
  5: 'Module 5: Final Wrap-up',
}

const MODULE_IDS = [1, 2, 3, 4, 5]

const SECTIONS = [
  { key: 'DroneSection', label: 'Drone Course' },
  { key: 'AiSection', label: 'Artificial Intelligence Course' },
]

const getInitials = (name) => {
  if (!name) return 'U'
  return name
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0].toUpperCase())
    .join('')
}

const ProfilePage = () => {
  const [user, setUser] = useState(null)
  const [skills, setSkills] = useState({ DroneSection: {}, AiSection: {} })
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const storedUser = getStoredUser()
    setUser(storedUser)

    const userId = getCurrentUserId()

    if (!userId) {
      setLoading(false)
      return
    }

    fetchUserSkills(userId).then((data) => {
      setSkills({
        DroneSection: data?.DroneSection || {},
        AiSection: data?.AiSection || {},
      })
      setLoading(false)
    })
  }, [])

  const getPercentFor = (sectionKey, moduleId) => {
    return skills?.[sectionKey]?.[`module${moduleId}`]?.percent ?? 0
  }

  // Module 1 is always open. Every other module unlocks only once the
  // previous module has hit 100%, within that same section.
  const isUnlocked = (sectionKey, moduleId) =>
    moduleId === 1 || getPercentFor(sectionKey, moduleId - 1) >= 100

  return (
    <div className="profile_pg_wrapper">
      <div className="profile_pg_header_card">
        <div className="profile_pg_avatar">{getInitials(user?.fullName)}</div>
        <div className="profile_pg_header_info">
          <h2 className="profile_pg_name">{user?.fullName || 'Guest User'}</h2>
          <p className="profile_pg_email">{user?.email || '—'}</p>
          {user?.phoneNumber && <p className="profile_pg_phone">{user.phoneNumber}</p>}
        </div>
      </div>

      {loading ? (
        <p className="profile_pg_loading">Loading your progress...</p>
      ) : (
        SECTIONS.map((section) => (
          <div key={section.key} className="profile_pg_section_block">
            <div className="profile_pg_section_head">
              <h3 className="profile_pg_section_title">{section.label} — Progress</h3>
              <p className="profile_pg_section_sub">
                Each module unlocks once the one before it is 100% watched.
              </p>
            </div>

            <div className="profile_pg_grid">
              {MODULE_IDS.map((moduleId) => {
                const percent = getPercentFor(section.key, moduleId)
                const unlocked = isUnlocked(section.key, moduleId)
                const isComplete = percent >= 100

                return (
                  <div
                    key={`${section.key}-${moduleId}`}
                    className={`profile_pg_card ${!unlocked ? 'profile_pg_card_locked' : ''}`}
                  >
                    <div className="profile_pg_card_top">
                      <span className="profile_pg_card_num">Module {moduleId}</span>
                      {!unlocked && (
                        <span className="profile_pg_lock_badge">&#128274; Locked</span>
                      )}
                      {unlocked && isComplete && (
                        <span className="profile_pg_done_badge">&#10003; 100% Complete</span>
                      )}
                    </div>

                    <h4 className="profile_pg_card_title">{MODULE_TITLES[moduleId]}</h4>

                    {unlocked ? (
                      <>
                        <div className="profile_pg_progress_track">
                          <div
                            className="profile_pg_progress_fill"
                            style={{ width: `${percent}%` }}
                          />
                        </div>
                        <span className="profile_pg_progress_text">
                          {isComplete ? '100% Complete' : `${percent}% watched`}
                        </span>
                      </>
                    ) : (
                      <p className="profile_pg_locked_text">
                        Complete Module {moduleId - 1} to unlock this module.
                      </p>
                    )}
                  </div>
                )
              })}
            </div>
          </div>
        ))
      )}
    </div>
  )
}

export default ProfilePage